// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/users/InvitationQueue.jsx

import React, { useMemo, useState } from "react";
import { Button } from "../../../design-system/components";
import UserStatusBadge from "./UserStatusBadge";

function formatDate(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}

function roleLabel(value) {
  return String(value || "No role")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function InvitationQueue({
  invitations = [],
  invitationAction = {},
  onResend,
  onCancel,
}) {
  const [showHistory, setShowHistory] = useState(false);

  const visible = useMemo(
    () => invitations.filter((item) => showHistory || item.status === "pending"),
    [invitations, showHistory]
  );

  const pending = invitations.filter((item) => item.status === "pending").length;

  return (
    <section className="enterprise-admin-invitation-queue">
      <div className="enterprise-admin-card-heading">
        <div>
          <p className="enterprise-admin-eyebrow">Invitation lifecycle</p>
          <h3>Member invitations</h3>
          <span>{pending} pending · {invitations.length} total</span>
        </div>

        <Button type="button" variant="secondary" size="small" className="enterprise-admin-link-button" onClick={() => setShowHistory((current) => !current)}>
          {showHistory ? "Show pending only" : "Show invitation history"}
        </Button>
      </div>

      {!visible.length ? (
        <div className="enterprise-admin-empty compact">
          <strong>No pending invitations</strong>
          <p>Previous invitations remain available when you open invitation history.</p>
        </div>
      ) : (
        <div className="enterprise-admin-invitation-list">
          {visible.map((invite) => {
            const pendingInvite = invite.status === "pending";
            const manualDelivery = invite.metadata?.delivery_mode === "manual_foundation";
            const busy = invitationAction.invitationId === invite.id;
            const busyAction = busy ? invitationAction.action : null;

            return (
              <article className={`enterprise-admin-invitation-card${busy ? " is-busy" : ""}`} key={invite.id} aria-busy={busy}>
                <div className="enterprise-admin-invitation-main">
                  <strong>{invite.invited_name || invite.email}</strong>
                  <span>{invite.email}</span>
                  <small>
                    {manualDelivery
                      ? "Invitation recorded — send the access instructions through your approved process"
                      : "Identity invitation delivery recorded"}
                  </small>
                </div>

                <div className="enterprise-admin-invitation-actions">
                  <Button type="button" variant="secondary" size="small" disabled={busy || !pendingInvite} onClick={() => onResend?.(invite.id)}>
                    {busyAction === "resend" ? "Resending…" : "Resend invitation"}
                  </Button>
                  <Button type="button" variant="danger" size="small" className="enterprise-admin-danger-button" disabled={busy || !pendingInvite} onClick={() => onCancel?.(invite.id)}>
                    {busyAction === "cancel" ? "Canceling…" : "Cancel invitation"}
                  </Button>
                </div>

                <dl className="enterprise-admin-invitation-facts">
                  <div className="enterprise-admin-invitation-fact">
                    <dt>Status</dt>
                    <dd><UserStatusBadge status={invite.status} /></dd>
                  </div>
                  <div className="enterprise-admin-invitation-fact">
                    <dt>Role</dt>
                    <dd>{roleLabel(invite.role_key)}</dd>
                  </div>
                  <div className="enterprise-admin-invitation-fact enterprise-admin-invitation-fact-wide">
                    <dt>Last sent</dt>
                    <dd>{formatDate(invite.last_sent_at)}</dd>
                  </div>
                  <div className="enterprise-admin-invitation-fact enterprise-admin-invitation-fact-attempts">
                    <dt>Resends</dt>
                    <dd>{invite.resend_count || 0}</dd>
                  </div>
                </dl>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
